import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import { orderBy as orderByFn } from 'lodash'
import { useMemo, useState } from 'react'
import styled from 'styled-components'

import { Pill } from '../../components/Pill/Pill'
import { Text } from '../../components/Text/Text'
import { getSafetyViolationBasicName, SafetyViolation } from '../../models/safety'
import { bgGray, darkGray, error, errorD1, lightGray1, primaryBlack } from '../../theme/colors'
import { SafetyDashboardCard } from './SafetyDashboard.styles'

interface SafetyDashboardViolationsProps {
  selectedSection: string
  violations: SafetyViolation[]
}

type Order = 'asc' | 'desc'

export function SafetyDashboardViolations({ selectedSection, violations }: SafetyDashboardViolationsProps) {
  const [order, setOrder] = useState<Order>('desc')
  const [orderBy, setOrderBy] = useState<keyof SafetyViolation>('violationDate')

  const createSortHandler = (property: keyof SafetyViolation) => () => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  function filterViolations() {
    if (selectedSection === 'oos') {
      return violations.filter((v) => v.isOos)
    }

    return violations.filter((v) => v.basic === selectedSection)
  }

  const data = filterViolations()
  const sortedData = useMemo(() => orderByFn(data, orderBy, order), [order, orderBy, data])

  function renderTitle() {
    if (selectedSection === 'oos') return 'Out of service'
    return getSafetyViolationBasicName(selectedSection)
  }

  function renderDescription(data: SafetyViolation) {
    if (selectedSection === 'oos')
      return (
        <>
          <Text variant="header3" fontColor={primaryBlack} mb={0.5}>
            {getSafetyViolationBasicName(data.basic)}
          </Text>
          <Text variant="paragraph2" fontColor={primaryBlack}>
            {data.violationDescription}
          </Text>
        </>
      )
    return (
      <>
        {data.isOos && (
          <Pill background={error} color={errorD1} mb={1}>
            OOS
          </Pill>
        )}
        <Text variant="paragraph2" fontColor={primaryBlack}>
          {data.violationDescription}
        </Text>
      </>
    )
  }

  return (
    <SafetyDashboardCard>
      <Text variant="uppercaseLabel2" mb={2}>
        {renderTitle()} violations
      </Text>

      <TableWrap>
        <TableContainer>
          <Table sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow sx={TableHeadRowStyles}>
                <TableCell width="140px" sortDirection={orderBy === 'violationDate' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'violationDate'}
                    direction={orderBy === 'violationDate' ? order : 'asc'}
                    onClick={createSortHandler('violationDate')}
                  >
                    <Text variant="label3" fontColor={lightGray1}>
                      Date
                    </Text>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <Text variant="label3" fontColor={lightGray1}>
                    Violation
                  </Text>
                </TableCell>
                <TableCell width="110px" sortDirection={orderBy === 'vin' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'vin'}
                    direction={orderBy === 'vin' ? order : 'asc'}
                    onClick={createSortHandler('vin')}
                  >
                    <Text variant="label3" fontColor={lightGray1}>
                      Truck VIN
                    </Text>
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  width="150px"
                  sortDirection={orderBy === 'violationTotalSeverityWeight' ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === 'violationTotalSeverityWeight'}
                    direction={orderBy === 'violationTotalSeverityWeight' ? order : 'asc'}
                    onClick={createSortHandler('violationTotalSeverityWeight')}
                  >
                    <Text variant="label3" fontColor={lightGray1}>
                      Total Weight
                    </Text>
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((row) => (
                <TableRow
                  key={`${row.violationDate?.getTime()}${row.reportNumber}${row.violationDescription}${
                    row.violationDescription
                  }${row.violationCode}`}
                  sx={TableRowStyles}
                >
                  <TableCell>
                    <Text variant="paragraph1" fontColor={darkGray}>
                      {row.violationDate?.toLocaleDateString('en-US', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      }) || '-'}
                    </Text>
                  </TableCell>
                  <TableCell>{renderDescription(row)}</TableCell>
                  <TableCell>
                    <Text variant="paragraph1" fontColor={darkGray}>
                      {row.vin}
                    </Text>
                  </TableCell>
                  <TableCell align="right">
                    <Text variant="paragraph1" fontColor={darkGray}>
                      {row.violationTotalSeverityWeight}
                    </Text>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TableWrap>
    </SafetyDashboardCard>
  )
}

const TableWrap = styled.div`
  margin: 0 -32px;
  width: calc(100% + 64px);
`

const TableHeadRowStyles = {
  th: { padding: '12px 8px' },
  'td:first-child, th:first-child': { paddingLeft: '32px' },
  'td:last-child, th:last-child': { paddingRight: '32px' },
  'td, th': { borderColor: bgGray, borderWeight: '2px' },
}

const TableRowStyles = {
  ...TableHeadRowStyles,
  '&:last-child td, &:last-child th': { border: 0 },
  td: { verticalAlign: 'top', padding: '25px 8px' },
}
